import { upsertPerformanceVesting } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	PerformanceVestingMetricType,
	UpsertPerformanceVestingDto,
	VestingTermRes,
} from '@dealstack/shared/lib/generated';
import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

export const usePerformanceVestingMutations = (
	currentVestingTerm: VestingTermRes,
	refetchVestingTerms: () => Promise<unknown>
) => {
	const upsertPerformanceVestingRequest = useMutation({
		mutationFn: upsertPerformanceVesting,
	});

	const onUpsertPerformanceVesting = useCallback(
		(args: UpsertPerformanceVestingDto) => {
			upsertPerformanceVestingRequest.mutate(args, {
				onSuccess: async () => {
					await refetchVestingTerms();
				},
			});
		},
		[upsertPerformanceVestingRequest, refetchVestingTerms]
	);

	const onMetricTypeChange = useCallback(
		(metricType: PerformanceVestingMetricType) => {
			const existingSubjectIds =
				currentVestingTerm.performanceVestingTerm?.measurementSubjects?.map(
					(subject) => subject.personId
				) ?? [];

			onUpsertPerformanceVesting({
				vestingTermId: currentVestingTerm.id,
				metricType,
				measurementSubjectPersonIds: existingSubjectIds,
				hurdles: [],
			});
		},
		[currentVestingTerm, onUpsertPerformanceVesting]
	);

	const onMeasurementSubjectsChange = useCallback(
		(personIds: string[]) => {
			const metricType =
				currentVestingTerm.performanceVestingTerm?.metricType ??
				PerformanceVestingMetricType.MOIC;

			onUpsertPerformanceVesting({
				vestingTermId: currentVestingTerm.id,
				metricType,
				measurementSubjectPersonIds: personIds,
			});
		},
		[currentVestingTerm, onUpsertPerformanceVesting]
	);

	const onTiersChange = useCallback(
		(hurdles: UpsertPerformanceVestingDto['hurdles']) => {
			const metricType =
				currentVestingTerm.performanceVestingTerm?.metricType ??
				PerformanceVestingMetricType.MOIC;
			const existingSubjectIds =
				currentVestingTerm.performanceVestingTerm?.measurementSubjects?.map(
					(subject) => subject.personId
				) ?? [];

			onUpsertPerformanceVesting({
				vestingTermId: currentVestingTerm.id,
				metricType,
				measurementSubjectPersonIds: existingSubjectIds,
				hurdles,
			});
		},
		[currentVestingTerm, onUpsertPerformanceVesting]
	);

	return useMemo(
		() => ({
			onMetricTypeChange,
			onMeasurementSubjectsChange,
			onTiersChange,
		}),
		[onMetricTypeChange, onMeasurementSubjectsChange, onTiersChange]
	);
};
